import { useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { TrendingUp, TrendingDown, BarChart3, Target, Clock, DollarSign, User, Award, Filter } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

interface CapitolTrade {
  politician: string;
  party: string;
  chamber: string;
  state: string;
  issuer: string;
  ticker: string;
  published: string;
  traded: string;
  filedAfter: string;
  owner: string;
  type: string;
  size: string;
  price: string;
  tradeUrl: string;
}

interface PoliticianScorecardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  politicianName: string;
  allTrades: CapitolTrade[];
  onFilterByPolitician?: (name: string) => void;
}

const SIZE_ORDER = ['1K–15K', '15K–50K', '50K–100K', '100K–250K', '250K–500K', '500K–1M', '1M–5M', '5M+'];

export const PoliticianScorecard = ({ open, onOpenChange, politicianName, allTrades, onFilterByPolitician }: PoliticianScorecardProps) => {
  const politicianTrades = useMemo(
    () => allTrades.filter(t => t.politician === politicianName),
    [allTrades, politicianName]
  );

  const stats = useMemo(() => {
    if (politicianTrades.length === 0) return null;

    const first = politicianTrades[0];
    const buys = politicianTrades.filter(t => ['buy', 'purchase'].includes(t.type.toLowerCase()));
    const sells = politicianTrades.filter(t => ['sell', 'sale'].includes(t.type.toLowerCase()));

    // Top tickers
    const tickerCounts: Record<string, number> = {};
    politicianTrades.forEach(t => {
      if (t.ticker) tickerCounts[t.ticker] = (tickerCounts[t.ticker] || 0) + 1;
    });
    const topTickers = Object.entries(tickerCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8);

    // Top issuers
    const issuerCounts: Record<string, number> = {};
    politicianTrades.forEach(t => {
      if (t.issuer && t.issuer !== 'Unknown') issuerCounts[t.issuer] = (issuerCounts[t.issuer] || 0) + 1;
    });
    const topIssuers = Object.entries(issuerCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);

    // Size distribution
    const sizeCounts: Record<string, number> = {};
    politicianTrades.forEach(t => {
      if (t.size && t.size !== 'N/A') sizeCounts[t.size] = (sizeCounts[t.size] || 0) + 1;
    });
    const sizeData = SIZE_ORDER
      .filter(s => sizeCounts[s])
      .map(s => ({ size: s, count: sizeCounts[s] }));
    // Add any sizes not in ORDER
    Object.keys(sizeCounts).forEach(s => {
      if (!SIZE_ORDER.includes(s)) sizeData.push({ size: s, count: sizeCounts[s] });
    });

    // Trade type pie
    const typePie = [
      { name: 'Buys', value: buys.length, color: '#10b981' },
      { name: 'Sells', value: sells.length, color: '#f43f5e' },
      { name: 'Other', value: politicianTrades.length - buys.length - sells.length, color: '#6b7280' },
    ].filter(d => d.value > 0);

    // Average filing delay
    const delays = politicianTrades
      .map(t => parseInt(t.filedAfter))
      .filter(d => !isNaN(d));
    const avgDelay = delays.length > 0 ? Math.round(delays.reduce((a, b) => a + b, 0) / delays.length) : null;

    // Specialty: most traded sector/issuer
    const specialty = topIssuers.length > 0 ? topIssuers[0][0] : 'Diversified';

    // Buy/sell ratio score (0-100, 50 = balanced)
    const total = buys.length + sells.length;
    const buyRatio = total > 0 ? Math.round((buys.length / total) * 100) : 50;

    // Activity score based on trade count
    const activityScore = Math.min(100, Math.round((politicianTrades.length / 20) * 100));

    return {
      party: first.party,
      chamber: first.chamber,
      state: first.state,
      totalTrades: politicianTrades.length,
      buys: buys.length,
      sells: sells.length,
      topTickers,
      topIssuers,
      sizeData,
      typePie,
      avgDelay,
      specialty,
      buyRatio,
      activityScore,
    };
  }, [politicianTrades]);

  if (!stats) return null;

  const getPartyBadgeClass = (party: string) => {
    const p = party.toLowerCase();
    if (p.includes('republican')) return 'bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30';
    if (p.includes('democrat')) return 'bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30';
    return 'bg-muted text-muted-foreground';
  };

  const getChamberBadgeClass = (chamber: string) => {
    if (chamber === 'House') return 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30';
    if (chamber === 'Senate') return 'bg-violet-500/15 text-violet-700 dark:text-violet-400 border-violet-500/30';
    return 'bg-muted text-muted-foreground';
  };

  const synopsis = (() => {
    const bias = stats.buyRatio > 60 ? 'bullish bias (more buying than selling)'
      : stats.buyRatio < 40 ? 'bearish bias (more selling than buying)'
      : 'balanced trading activity';
    const sizeDesc = stats.sizeData.length > 0
      ? `Most trades fall in the ${stats.sizeData.sort((a, b) => b.count - a.count)[0].size} range.`
      : '';
    const delayDesc = stats.avgDelay !== null
      ? `Average filing delay is ${stats.avgDelay} days after trade.`
      : '';
    return `${politicianName} (${stats.party}, ${stats.chamber}${stats.state ? ` – ${stats.state}` : ''}) has made ${stats.totalTrades} recorded trade(s) with a ${bias}. Specialty focus: ${stats.specialty}. ${sizeDesc} ${delayDesc}`;
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-lg">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="h-5 w-5 text-primary" />
            </div>
            {politicianName}
          </DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-2 pt-1">
            <Badge className={getPartyBadgeClass(stats.party)}>{stats.party}</Badge>
            <Badge className={getChamberBadgeClass(stats.chamber)}>{stats.chamber}</Badge>
            {stats.state && <Badge variant="outline">{stats.state}</Badge>}
            {onFilterByPolitician && (
              <Button
                variant="outline"
                size="sm"
                className="ml-auto h-7 text-xs gap-1"
                onClick={() => onFilterByPolitician(politicianName)}
              >
                <Filter className="h-3 w-3" />
                Filter table
              </Button>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Synopsis */}
        <Card className="bg-muted/30 border-muted">
          <CardContent className="p-4">
            <p className="text-sm text-foreground leading-relaxed">{synopsis}</p>
          </CardContent>
        </Card>

        {/* Key Metrics Row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card>
            <CardContent className="p-3 text-center">
              <BarChart3 className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
              <p className="text-xl font-bold text-foreground">{stats.totalTrades}</p>
              <p className="text-xs text-muted-foreground">Total Trades</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <TrendingUp className="h-4 w-4 mx-auto mb-1 text-emerald-600 dark:text-emerald-400" />
              <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">{stats.buys}</p>
              <p className="text-xs text-muted-foreground">Buys</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <TrendingDown className="h-4 w-4 mx-auto mb-1 text-rose-600 dark:text-rose-400" />
              <p className="text-xl font-bold text-rose-600 dark:text-rose-400">{stats.sells}</p>
              <p className="text-xs text-muted-foreground">Sells</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <Clock className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
              <p className="text-xl font-bold text-foreground">{stats.avgDelay ?? '—'}</p>
              <p className="text-xs text-muted-foreground">Avg Delay (days)</p>
            </CardContent>
          </Card>
        </div>

        {/* Scores */}
        <div className="space-y-3">
          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-muted-foreground">Buy/Sell Ratio</span>
              <span className="font-medium text-foreground">{stats.buyRatio}% buys</span>
            </div>
            <Progress value={stats.buyRatio} className="h-2" />
          </div>
          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-muted-foreground">Activity Score</span>
              <span className="font-medium text-foreground">{stats.activityScore}/100</span>
            </div>
            <Progress value={stats.activityScore} className="h-2" />
          </div>
        </div>

        {/* Trade Type Pie + Top Tickers */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Pie */}
          <Card>
            <CardContent className="p-4">
              <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-1.5">
                <Target className="h-3.5 w-3.5" /> Trade Type Breakdown
              </h4>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={stats.typePie} cx="50%" cy="50%" innerRadius={35} outerRadius={60} dataKey="value" nameKey="name">
                    {stats.typePie.map(e => <Cell key={e.name} fill={e.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }} />
                  <Legend wrapperStyle={{ fontSize: '11px' }} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Top Tickers */}
          <Card>
            <CardContent className="p-4">
              <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-1.5">
                <DollarSign className="h-3.5 w-3.5" /> Top Traded Tickers
              </h4>
              <div className="space-y-1.5">
                {stats.topTickers.map(([ticker, count]) => (
                  <div key={ticker} className="flex items-center justify-between">
                    <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">{ticker}</code>
                    <div className="flex items-center gap-2 flex-1 ml-3">
                      <div className="flex-1 bg-muted rounded-full h-1.5">
                        <div
                          className="bg-primary rounded-full h-1.5 transition-all"
                          style={{ width: `${(count / stats.topTickers[0][1]) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs text-muted-foreground w-6 text-right">{count}</span>
                    </div>
                  </div>
                ))}
                {stats.topTickers.length === 0 && (
                  <p className="text-xs text-muted-foreground">No ticker data available</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Specialty: Top Issuers */}
        {stats.topIssuers.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-1.5">
                <Award className="h-3.5 w-3.5" /> Specialty — Most Traded Companies
              </h4>
              <div className="space-y-2">
                {stats.topIssuers.map(([issuer, count], i) => (
                  <div key={issuer} className="flex items-center gap-3">
                    <span className="text-xs font-bold text-muted-foreground w-5">#{i + 1}</span>
                    <span className="text-sm text-foreground flex-1 truncate">{issuer}</span>
                    <Badge variant="secondary" className="text-xs">{count} trade{count > 1 ? 's' : ''}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Size Distribution */}
        {stats.sizeData.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <h4 className="text-sm font-medium text-foreground mb-2">Trade Size Distribution</h4>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={stats.sizeData} margin={{ left: 0, right: 10, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                  <XAxis dataKey="size" tick={{ fontSize: 10 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }} />
                  <Bar dataKey="count" fill="hsl(var(--primary))" name="Trades" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </DialogContent>
    </Dialog>
  );
};
